import { useState, useEffect } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";

export default function LanguageLabels() {
  const [labels, setLabels] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => apiFetch("/labels").then(setLabels).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const update = (key, value) => setLabels((ls) => ls.map((l) => (l.key === key ? { ...l, label: value } : l)));

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch("/labels", { method: "PUT", body: JSON.stringify({ labels: labels.map((l) => ({ key: l.key, label: l.label })) }) });
      toast.success("Labels saved", { description: "New wording will appear across the workspace." });
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  if (!labels) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;
  const categories = [...new Set(labels.map((l) => l.category))];

  return (
    <div className="animate-fade-up">
      <PageHeader eyebrow="Settings" title="Language & Labels"
        subtitle="User-facing wording is not hard-coded. Edit any label to match Nirvana's warm, simple voice."
        actions={
          <Button variant="outline" className="gap-2" onClick={load} data-testid="reset-labels">
            <RotateCcw className="h-4 w-4" /> Reload
          </Button>
        } />

      <div className="space-y-6">
        {categories.map((cat) => (
          <Card key={cat} className="overflow-hidden shadow-soft" data-testid={`label-cat-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="border-b border-border bg-muted/40 px-5 py-3 font-heading text-lg font-semibold">{cat}</div>
            <div className="divide-y divide-border">
              {labels.filter((l) => l.category === cat).map((l) => (
                <div key={l.key} className="grid grid-cols-1 gap-3 px-5 py-4 sm:grid-cols-[220px_1fr]">
                  <div>
                    <div className="font-mono text-xs text-muted-foreground">{l.key}</div>
                    <div className="mt-1 text-xs text-muted-foreground">e.g. “{l.example}”</div>
                  </div>
                  <Input value={l.label} onChange={(e) => update(l.key, e.target.value)} data-testid={`label-${l.key}`} />
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="save-labels">{saving ? "Saving…" : "Save Labels"}</Button>
      </div>
    </div>
  );
}
